import {useState} from "react";
import PropTypes from "prop-types";
import {useNavigate} from "react-router-dom";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import CircleIcon from "@mui/icons-material/Circle";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";
import {useNotifications} from "../../context/NotificationProvider";
import {markRead as apiMarkRead} from "../../api/notification/notificationApiClient";

/**
 * Bell icon for the dashboard navbar. Shows the unread count as a
 * badge and a dropdown with the most recent live notifications.
 *
 * Click an item → marks it read in place, leaving the dropdown open.
 * "View all" → /notifications.
 */
function NotificationBell({light = false}) {
    const navigate = useNavigate();
    const {unreadCount, recent, decrementUnread, upsertRecent} = useNotifications();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    // Stays in the dropdown. It used to close the menu and navigate to
    // /notifications on every click, which meant the bell could never be used
    // to *read* notifications — the only way to see one was to leave the page
    // you were on. "View all" below is the deliberate way to the full list.
    const handleItemClick = async (n) => {
        if (n.status !== "UNREAD") return;
        try {
            const updated = await apiMarkRead(n.id);
            upsertRecent(updated);
            decrementUnread();
        } catch {
            // non-fatal
        }
    };

    const handleViewAll = () => {
        handleClose();
        navigate("/notifications");
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleOpen}
                aria-label="notifications"
                sx={({palette}) => ({
                    color: light ? palette.white.main : palette.dark.main,
                })}
            >
                <Badge
                    badgeContent={unreadCount > 99 ? "99+" : unreadCount}
                    color="error"
                    invisible={unreadCount === 0}
                    overlap="circular"
                    sx={{"& .MuiBadge-badge": {fontSize: 10, height: 18, minWidth: 18}}}
                >
                    <Icon fontSize="medium">notifications</Icon>
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                transformOrigin={{vertical: "top", horizontal: "right"}}
                slotProps={{paper: {sx: {width: 360, maxHeight: 420, mt: 1}}}}
            >
                <SoftBox px={2} py={1.25} display="flex" justifyContent="space-between" alignItems="center">
                    <SoftTypography variant="button" fontWeight="bold" color="dark" textTransform="uppercase">
                        Notifications
                    </SoftTypography>
                    {unreadCount > 0 && (
                        <SoftTypography variant="caption" color="text">
                            {unreadCount} unread
                        </SoftTypography>
                    )}
                </SoftBox>
                <Divider sx={{my: 0}} />

                {recent.length === 0 ? (
                    <SoftBox px={2} py={3} textAlign="center">
                        <SoftTypography variant="caption" color="text" opacity={0.65}>
                            You have no notifications.
                        </SoftTypography>
                    </SoftBox>
                ) : (
                    recent.map((n) => (
                        <MenuItem
                            key={n.id}
                            onClick={() => handleItemClick(n)}
                            sx={{
                                whiteSpace: "normal",
                                alignItems: "flex-start",
                                py: 1,
                                px: 2,
                            }}
                        >
                            <SoftBox sx={{width: 12, mt: 0.5, mr: 1}}>
                                {n.status === "UNREAD" ? (
                                    <CircleIcon sx={{fontSize: 8, color: "primary.main"}} />
                                ) : null}
                            </SoftBox>
                            <SoftBox sx={{flex: 1, minWidth: 0}}>
                                <SoftTypography variant="button" fontWeight="medium" color="dark">
                                    {n.title}
                                </SoftTypography>
                                <SoftTypography variant="caption" color="text" sx={{display: "block", opacity: 0.75}}>
                                    {n.body}
                                </SoftTypography>
                            </SoftBox>
                        </MenuItem>
                    ))
                )}

                <Divider sx={{my: 0}} />
                <MenuItem onClick={handleViewAll} sx={{justifyContent: "center"}}>
                    <SoftTypography variant="button" fontWeight="medium" color="primary">
                        View all
                    </SoftTypography>
                </MenuItem>
            </Menu>
        </>
    );
}

NotificationBell.defaultProps = {
    light: false,
};

NotificationBell.propTypes = {
    light: PropTypes.bool,
};

export default NotificationBell;
